import type { ReactNode } from "react";
import { cn } from "../../lib/utils";

interface SectionProps {
  /** Heading shown at the top of the panel. */
  title?: ReactNode;
  /** Optional supporting copy under the title. */
  description?: ReactNode;
  /** Optional control rendered on the right of the header row (e.g. a button). */
  action?: ReactNode;
  className?: string;
  /** Spacing applied between the panel's direct children. */
  contentClassName?: string;
  children: ReactNode;
}

/**
 * A visually distinct panel that groups related content on its own surface
 * with a header, generous padding, and breathing room between children.
 *
 * This is the core building block of the streamlined UI: every page composes
 * its content from these panels so detail screens, forms, and settings all
 * share the same rhythm and separation rather than one undivided column.
 */
export function Section({
  title,
  description,
  action,
  className,
  contentClassName,
  children,
}: SectionProps): ReactNode {
  const hasHeader = title !== undefined || action !== undefined;
  return (
    <section
      className={cn(
        "space-y-6 rounded-md bg-surface-container-lowest p-6",
        className,
      )}
    >
      {hasHeader ? (
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            {title !== undefined ? (
              <h2 className="text-headline-md">{title}</h2>
            ) : null}
            {description !== undefined ? (
              <p className="text-label-sm text-on-surface-variant">
                {description}
              </p>
            ) : null}
          </div>
          {action}
        </div>
      ) : null}
      <div className={cn("space-y-5", contentClassName)}>{children}</div>
    </section>
  );
}
